import React from "react";
import { Modal, Button, Input, Tooltip, Tag, Radio } from "antd";
import type { RadioChangeEvent } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import socketEmitter from "../socketConnection/MonitoringWebSocketEmitter";
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";
import { BitMaskMessageContent } from "../../../flatbuffers/generated/ln2/bit-mask-message-content";
import { OneHotMessageContent } from "../../../flatbuffers/generated/ln2/one-hot-message-content";
import { FaultContent } from "../../../flatbuffers/generated/fault/fault-content";
import { ElevatorFault } from "../../../flatbuffers/generated/fault/elevator-fault";
import { EscalatorFault } from "../../../flatbuffers/generated/fault/escalator-fault";
import { DeviceTrafficMessage } from "../../../flatbuffers/generated/traffic/device-traffic-message";
import { ElevatorTraffic } from "../../../flatbuffers/generated/traffic/elevator-traffic";
import { EscalatorTraffic } from "../../../flatbuffers/generated/traffic/escalator-traffic";
import { DeviceUsageContent } from "../../../flatbuffers/generated/usage/device-usage-content";
import { ElevatorUsage } from "../../../flatbuffers/generated/usage/elevator-usage";
import { EscalatorUsage } from "../../../flatbuffers/generated/usage/escalator-usage";
import { DeviceServiceContent } from "../../../flatbuffers/generated/service/device-service-content";
import { ElevatorService } from "../../../flatbuffers/generated/service/elevator-service";
import { EscalatorService } from "../../../flatbuffers/generated/service/escalator-service";
import { DeviceStatusContent } from "../../../flatbuffers/generated/status/device-status-content";
import { ElevatorStatusContent } from "../../../flatbuffers/generated/status/elevator-status-content";
import { EscalatorStatusContent } from "../../../flatbuffers/generated/status/escalator-status-content";
import { ConnectionStatus } from "../../../flatbuffers/generated/status/connection-status";
import { DoorStatus } from "../../../flatbuffers/generated/status/door-status";
import { ModeStatus } from "../../../flatbuffers/generated/status/mode-status";
import { TrafficStatus } from "../../../flatbuffers/generated/status/traffic-status";
import { LoadWeigherStatus } from "../../../flatbuffers/generated/status/load-weigher-status";
import { EarthquakeStatus } from "../../../flatbuffers/generated/status/earthquake-status";
import { EarthquakeProtectionStatus } from "../../../flatbuffers/generated/status/earthquake-protection-status";
import { Status } from "../../../flatbuffers/generated/status/status";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";

type DecodedFrontEndMessagesModalProps = {
  open: boolean;
  onClose: () => void;
};

type PreDispatchMessage = {
  timestamp: number;
  bankId: string;
  liftId: string;
  schemaFrontEndMessageJson: string;
  socketMessageLiftJson: string;
};

type ContentTypeFilter = "all" | "OneHotMessage" | "BitMaskMessage";

const getContentTypeFromSchemaJson = (
  schemaFrontEndMessageJson: string,
): "OneHotMessage" | "BitMaskMessage" | null => {
  try {
    const parsed = JSON.parse(schemaFrontEndMessageJson) as Record<string, unknown>;
    const msg = parsed.msg as Record<string, unknown> | undefined;
    const encoded = msg?.msg as Record<string, unknown> | undefined;
    const contentType = encoded?.contentType;

    if (contentType === EncodedMessageContent.OneHotMessage || contentType === "OneHotMessage") {
      return "OneHotMessage";
    }
    if (contentType === EncodedMessageContent.BitMaskMessage || contentType === "BitMaskMessage") {
      return "BitMaskMessage";
    }
    return null;
  } catch {
    return null;
  }
};

const getEnumName = (
  enumObject: Record<string, string | number>,
  value: unknown,
): string | number | unknown => {
  if (typeof value !== "number") return value;
  const enumName = (enumObject as unknown as Record<number, string>)[value];
  return enumName ?? value;
};

const translateDecodedPayloadEnums = (payload: unknown): unknown => {
  if (!payload || typeof payload !== "object") return payload;

  const translated = JSON.parse(JSON.stringify(payload)) as Record<string, unknown>;
  const msg = translated.msg;
  if (!msg || typeof msg !== "object") return translated;

  const msgObject = msg as Record<string, unknown>;
  const encodedMsg = msgObject.msg;
  if (!encodedMsg || typeof encodedMsg !== "object") return translated;

  const encodedMsgObject = encodedMsg as Record<string, unknown>;
  const rawContentType = encodedMsgObject.contentType;
  encodedMsgObject.contentType = getEnumName(EncodedMessageContent, encodedMsgObject.contentType);

  const content = encodedMsgObject.content;
  if (!content || typeof content !== "object") return translated;

  const contentObject = content as Record<string, unknown>;
  const rawMessageType = contentObject.messageType;
  if (rawContentType === EncodedMessageContent.BitMaskMessage) {
    contentObject.messageType = getEnumName(BitMaskMessageContent, rawMessageType);
  } else if (rawContentType === EncodedMessageContent.OneHotMessage) {
    contentObject.messageType = getEnumName(OneHotMessageContent, rawMessageType);
  } else {
    const bitMaskName = getEnumName(BitMaskMessageContent, rawMessageType);
    const oneHotName = getEnumName(OneHotMessageContent, rawMessageType);
    contentObject.messageType = typeof bitMaskName === "string" ? bitMaskName : oneHotName;
  }

  const nestedMessage = contentObject.message;
  if (!nestedMessage || typeof nestedMessage !== "object") return translated;

  const nestedMessageObject = nestedMessage as Record<string, unknown>;
  const rawNestedMessageType = nestedMessageObject.messageType;

  if (
    rawContentType === EncodedMessageContent.OneHotMessage &&
    typeof rawMessageType === "number" &&
    rawMessageType === OneHotMessageContent.Usage_UsageMessage
  ) {
    nestedMessageObject.messageType = getEnumName(DeviceUsageContent, rawNestedMessageType);

    const nestedUsage = nestedMessageObject.message;
    if (nestedUsage && typeof nestedUsage === "object") {
      const nestedUsageObject = nestedUsage as Record<string, unknown>;
      const rawUsageMessage = nestedUsageObject.message;

      if (typeof rawUsageMessage === "number") {
        if (rawNestedMessageType === DeviceUsageContent.ElevatorUsageMessage) {
          nestedUsageObject.message = getEnumName(ElevatorUsage, rawUsageMessage);
        }
        if (rawNestedMessageType === DeviceUsageContent.EscalatorUsageMessage) {
          nestedUsageObject.message = getEnumName(EscalatorUsage, rawUsageMessage);
        }
      }
    }
  }

  if (
    rawContentType === EncodedMessageContent.BitMaskMessage &&
    typeof rawMessageType === "number" &&
    rawMessageType === BitMaskMessageContent.Status_StatusMessage
  ) {
    const rawDeviceStatusType = nestedMessageObject.statusType;
    nestedMessageObject.statusType = getEnumName(DeviceStatusContent, rawDeviceStatusType);

    const nestedStatus = nestedMessageObject.status;
    if (nestedStatus && typeof nestedStatus === "object") {
      const nestedStatusObject = nestedStatus as Record<string, unknown>;
      const rawStatusType = nestedStatusObject.statusType;

      if (rawDeviceStatusType === DeviceStatusContent.ElevatorStatusMessage) {
        nestedStatusObject.statusType = getEnumName(ElevatorStatusContent, rawStatusType);
      }
      if (rawDeviceStatusType === DeviceStatusContent.EscalatorStatusMessage) {
        nestedStatusObject.statusType = getEnumName(EscalatorStatusContent, rawStatusType);
      }

      const deepestStatus = nestedStatusObject.status;
      if (deepestStatus && typeof deepestStatus === "object") {
        const deepestStatusObject = deepestStatus as Record<string, unknown>;
        const rawDeepStatusMessage = deepestStatusObject.message;

        if (typeof rawDeepStatusMessage === "number" && typeof rawStatusType === "number") {
          if (
            rawStatusType === ElevatorStatusContent.EarthquakeProtectionMessage ||
            rawStatusType === EscalatorStatusContent.EarthquakeProtectionMessage
          ) {
            deepestStatusObject.message = getEnumName(
              EarthquakeProtectionStatus,
              rawDeepStatusMessage,
            );
          }
          if (
            rawStatusType === ElevatorStatusContent.EarthquakeStatusMessage ||
            rawStatusType === EscalatorStatusContent.EarthquakeStatusMessage
          ) {
            deepestStatusObject.message = getEnumName(EarthquakeStatus, rawDeepStatusMessage);
          }
          if (
            rawStatusType === ElevatorStatusContent.GeneralStatusMessage ||
            rawStatusType === EscalatorStatusContent.GeneralStatusMessage
          ) {
            deepestStatusObject.message = getEnumName(Status, rawDeepStatusMessage);
          }
          if (
            rawStatusType === ElevatorStatusContent.ConnectionStatusMessage ||
            rawStatusType === EscalatorStatusContent.ConnectionStatusMessage
          ) {
            deepestStatusObject.message = getEnumName(ConnectionStatus, rawDeepStatusMessage);
          }
          if (
            rawStatusType === ElevatorStatusContent.ModeStatusMessage ||
            rawStatusType === EscalatorStatusContent.ModeStatusMessage
          ) {
            deepestStatusObject.message = getEnumName(ModeStatus, rawDeepStatusMessage);
          }
          if (rawStatusType === ElevatorStatusContent.DoorStatusMessage) {
            deepestStatusObject.message = getEnumName(DoorStatus, rawDeepStatusMessage);
          }
          if (
            rawStatusType === ElevatorStatusContent.TrafficStatusMessage ||
            rawStatusType === EscalatorStatusContent.TrafficStatusMessage
          ) {
            deepestStatusObject.message = getEnumName(TrafficStatus, rawDeepStatusMessage);
          }
          if (
            rawStatusType === ElevatorStatusContent.LoadWeigherStatusMessage ||
            rawStatusType === EscalatorStatusContent.LoadWeigherStatusMessage
          ) {
            deepestStatusObject.message = getEnumName(LoadWeigherStatus, rawDeepStatusMessage);
          }
        }
      }
    }
  }

  if (
    rawContentType === EncodedMessageContent.OneHotMessage &&
    typeof rawMessageType === "number" &&
    rawMessageType === OneHotMessageContent.Service_ServiceMessage
  ) {
    nestedMessageObject.messageType = getEnumName(DeviceServiceContent, rawNestedMessageType);

    const nestedService = nestedMessageObject.message;
    if (nestedService && typeof nestedService === "object") {
      const nestedServiceObject = nestedService as Record<string, unknown>;
      const rawServiceMessage = nestedServiceObject.message;

      if (typeof rawServiceMessage === "number") {
        if (rawNestedMessageType === DeviceServiceContent.ElevatorServiceMessage) {
          nestedServiceObject.message = getEnumName(ElevatorService, rawServiceMessage);
        }
        if (rawNestedMessageType === DeviceServiceContent.EscalatorServiceMessage) {
          nestedServiceObject.message = getEnumName(EscalatorService, rawServiceMessage);
        }
      }
    }
  }

  if (
    rawContentType === EncodedMessageContent.BitMaskMessage &&
    typeof rawMessageType === "number" &&
    rawMessageType === BitMaskMessageContent.Traffic_TrafficMessage
  ) {
    nestedMessageObject.messageType = getEnumName(DeviceTrafficMessage, rawNestedMessageType);

    const nestedTraffic = nestedMessageObject.message;
    if (nestedTraffic && typeof nestedTraffic === "object") {
      const nestedTrafficObject = nestedTraffic as Record<string, unknown>;
      const rawTrafficMessage = nestedTrafficObject.message;

      if (typeof rawTrafficMessage === "number") {
        if (rawNestedMessageType === DeviceTrafficMessage.ElevatorTrafficMessage) {
          nestedTrafficObject.message = getEnumName(ElevatorTraffic, rawTrafficMessage);
        }
        if (rawNestedMessageType === DeviceTrafficMessage.EscalatorTrafficMessage) {
          nestedTrafficObject.message = getEnumName(EscalatorTraffic, rawTrafficMessage);
        }
      }
    }
  }

  const rawFaultType = nestedMessageObject.faultType;
  nestedMessageObject.faultType = getEnumName(FaultContent, rawFaultType);

  const nestedFault = nestedMessageObject.fault;
  if (!nestedFault || typeof nestedFault !== "object") return translated;

  const nestedFaultObject = nestedFault as Record<string, unknown>;
  const rawFaultMessage = nestedFaultObject.message;
  if (typeof rawFaultMessage !== "number" || typeof rawFaultType !== "number") {
    return translated;
  }

  if (rawFaultType === FaultContent.ElevatorFaultMessage) {
    nestedFaultObject.message = getEnumName(ElevatorFault, rawFaultMessage);
  }
  if (rawFaultType === FaultContent.EscalatorFaultMessage) {
    nestedFaultObject.message = getEnumName(EscalatorFault, rawFaultMessage);
  }

  return translated;
};

const getServiceModeTagInfo = (
  socketMessageLiftJson: string,
): ReturnType<typeof getServiceModeDisplay> => {
  try {
    const parsed = JSON.parse(socketMessageLiftJson) as Record<string, unknown>;
    const serviceMode = parsed.serviceMode;

    if (!serviceMode || typeof serviceMode !== "object") return null;

    const serviceModeObject = serviceMode as Record<string, unknown>;
    const serviceContent = serviceModeObject.messageType;
    const serviceMessageContainer = serviceModeObject.message;

    if (
      typeof serviceContent !== "number" ||
      !serviceMessageContainer ||
      typeof serviceMessageContainer !== "object"
    ) {
      return null;
    }

    const serviceMessage = (serviceMessageContainer as Record<string, unknown>).message;
    if (typeof serviceMessage !== "number") return null;

    return getServiceModeDisplay(
      serviceContent as DeviceServiceContent,
      serviceMessage as ElevatorService | EscalatorService,
    );
  } catch {
    return null;
  }
};

const omitBankIdAndLiftId = (payload: unknown): unknown => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return payload;

  const { bankId: _bankId, liftId: _liftId, ...rest } = payload as Record<string, unknown>;
  return rest;
};

function DecodedFrontEndMessagesModal({
  open,
  onClose,
}: DecodedFrontEndMessagesModalProps): React.ReactElement {
  const [preDispatchMessages, setPreDispatchMessages] = React.useState<PreDispatchMessage[]>([]);
  const [preDispatchLiftFilter, setPreDispatchLiftFilter] = React.useState("");
  const [contentTypeFilter, setContentTypeFilter] = React.useState<ContentTypeFilter>("all");
  const [selectedPreDispatchMessageIndex, setSelectedPreDispatchMessageIndex] = React.useState<
    number | null
  >(null);

  const filteredPreDispatchMessages = React.useMemo(() => {
    const liftFilter = preDispatchLiftFilter.trim().toLowerCase();

    if (!liftFilter) return [];

    return preDispatchMessages.filter((entry) => {
      if (entry.liftId.trim().toLowerCase() !== liftFilter) return false;
      if (contentTypeFilter === "all") return true;

      return getContentTypeFromSchemaJson(entry.schemaFrontEndMessageJson) === contentTypeFilter;
    });
  }, [preDispatchMessages, preDispatchLiftFilter, contentTypeFilter]);

  React.useEffect(() => {
    if (filteredPreDispatchMessages.length === 0) {
      setSelectedPreDispatchMessageIndex(null);
      return;
    }

    if (
      selectedPreDispatchMessageIndex === null ||
      selectedPreDispatchMessageIndex >= filteredPreDispatchMessages.length
    ) {
      setSelectedPreDispatchMessageIndex(filteredPreDispatchMessages.length - 1);
    }
  }, [filteredPreDispatchMessages, selectedPreDispatchMessageIndex]);

  const selectedFilteredPreDispatchMessage = React.useMemo(() => {
    if (selectedPreDispatchMessageIndex === null) return null;
    return filteredPreDispatchMessages[selectedPreDispatchMessageIndex] ?? null;
  }, [filteredPreDispatchMessages, selectedPreDispatchMessageIndex]);

  const latestSchemaFrontEndPrettyJson = React.useMemo(() => {
    if (!selectedFilteredPreDispatchMessage) return "";

    try {
      const schemaFrontEndMessage = JSON.parse(
        selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson,
      );
      return JSON.stringify(
        omitBankIdAndLiftId(schemaFrontEndMessage),
        null,
        2,
      );
    } catch {
      return selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson;
    }
  }, [selectedFilteredPreDispatchMessage]);

  const selectedServiceModeInfo = React.useMemo(() => {
    if (!selectedFilteredPreDispatchMessage) return null;
    return getServiceModeTagInfo(selectedFilteredPreDispatchMessage.socketMessageLiftJson);
  }, [selectedFilteredPreDispatchMessage]);

  const selectedBitMaskTrafficPosition = React.useMemo(() => {
    if (!selectedFilteredPreDispatchMessage) return null;

    try {
      const parsed = JSON.parse(
        selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson,
      ) as Record<string, unknown>;
      const msg = parsed.msg as Record<string, unknown> | undefined;
      const encoded = msg?.msg as Record<string, unknown> | undefined;
      const contentType = encoded?.contentType;

      if (
        contentType !== EncodedMessageContent.BitMaskMessage &&
        contentType !== "BitMaskMessage"
      ) {
        return null;
      }

      const content = encoded?.content as Record<string, unknown> | undefined;
      const messageType = content?.messageType;
      if (
        messageType !== BitMaskMessageContent.Traffic_TrafficMessage &&
        messageType !== "Traffic_TrafficMessage"
      ) {
        return null;
      }

      const trafficWrapper = content?.message as Record<string, unknown> | undefined;
      const trafficPayload = trafficWrapper?.message as Record<string, unknown> | undefined;
      const position = trafficPayload?.position;

      if (typeof position === "number" || typeof position === "string") {
        return position;
      }

      return null;
    } catch {
      return null;
    }
  }, [selectedFilteredPreDispatchMessage]);

  const serviceModeInfoTooltip = (
    <div style={{ maxWidth: 420, fontSize: 12, lineHeight: 1.5 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Which messages can update this tag:</div>
      <div>
        - contentType = <code>OneHotMessage</code>
      </div>
      <div>
        - messageType = <code>Service_ServiceMessage</code>
      </div>
      <div>
        - inside message, it uses <code>message.message</code> (service code)
      </div>
      <div>
        Example: <code>EscalatorServiceMessage</code> + <code>message: 0</code> =&gt;{" "}
        <code>AUTO</code>
      </div>
      <div>
        If <code>active</code> is <code>true</code>, service mode is stored/updated and shown.
      </div>
      <div>
        If <code>active</code> is <code>false</code>, service mode is cleared and the tag has no
        service value (or visual fallback if enabled).
      </div>
      <div>
        Other <code>contentType</code> values, such as <code>BitMaskMessage</code> (for status /
        connection updates), do not update this service mode tag.
      </div>
    </div>
  );

  const selectedMessageContentType = React.useMemo(() => {
    if (!selectedFilteredPreDispatchMessage) return null;

    return getContentTypeFromSchemaJson(selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson);
  }, [selectedFilteredPreDispatchMessage]);

  const jsonBoxHighlightStyle = React.useMemo(() => {
    if (selectedMessageContentType === "OneHotMessage") {
      return {
        border: "1px solid rgba(255, 77, 79, 0.55)",
        background: "rgba(255, 77, 79, 0.07)",
      };
    }

    if (selectedMessageContentType === "BitMaskMessage") {
      return {
        border: "1px solid rgba(82, 196, 26, 0.55)",
        background: "rgba(82, 196, 26, 0.08)",
      };
    }

    return {
      border: "1px solid #d9d9d9",
      background: "transparent",
    };
  }, [selectedMessageContentType]);

  const latestSchemaFrontEndTranslatedPrettyJson = React.useMemo(() => {
    if (!selectedFilteredPreDispatchMessage) return "";

    try {
      const schemaFrontEndMessage = JSON.parse(
        selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson,
      );
      const translated = omitBankIdAndLiftId(translateDecodedPayloadEnums(schemaFrontEndMessage));
      return JSON.stringify(translated, null, 2);
    } catch {
      return selectedFilteredPreDispatchMessage.schemaFrontEndMessageJson;
    }
  }, [selectedFilteredPreDispatchMessage]);

  const onDownloadPreDispatchMessages = React.useCallback(() => {
    const exportPayload = filteredPreDispatchMessages.map((entry) => {
      try {
        const schemaFrontEndMessage = JSON.parse(entry.schemaFrontEndMessageJson);
        const translated = translateDecodedPayloadEnums(schemaFrontEndMessage);

        return {
          schemaFrontEndMessage: omitBankIdAndLiftId(schemaFrontEndMessage),
          schemaFrontEndMessageTranslated: omitBankIdAndLiftId(translated),
        };
      } catch {
        return {
          schemaFrontEndMessage: entry.schemaFrontEndMessageJson,
          schemaFrontEndMessageTranslated: entry.schemaFrontEndMessageJson,
        };
      }
    });

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
      type: "application/json",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `decoded_front_end_messages_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [filteredPreDispatchMessages]);

  React.useEffect(() => {
    const onPreDispatchMessage = (event: {
      timestamp?: number;
      bankId?: string;
      liftId?: string;
      schemaFrontEndMessageJson?: string;
      socketMessageLiftJson?: string;
      payloadJson?: string;
    }) => {
      const activeLiftFilter = preDispatchLiftFilter.trim().toLowerCase();
      if (!activeLiftFilter) return;

      const incomingLiftId = (event?.liftId ?? "").trim().toLowerCase();
      if (incomingLiftId !== activeLiftFilter) return;

      const schemaFrontEndMessageJson: string =
        event?.schemaFrontEndMessageJson ?? event?.payloadJson ?? "";
      const socketMessageLiftJson: string = event?.socketMessageLiftJson ?? "";
      if (!schemaFrontEndMessageJson || !socketMessageLiftJson) return;

      setPreDispatchMessages((prev) => [
        ...prev,
        {
          timestamp: event.timestamp ?? Date.now(),
          bankId: event.bankId ?? "",
          liftId: event.liftId ?? "",
          schemaFrontEndMessageJson,
          socketMessageLiftJson,
        },
      ]);
    };

    socketEmitter.on("preDispatchMessage", onPreDispatchMessage);
    return () => {
      socketEmitter.off("preDispatchMessage", onPreDispatchMessage);
    };
  }, [preDispatchLiftFilter]);

  React.useEffect(() => {
    setPreDispatchMessages([]);
    setSelectedPreDispatchMessageIndex(null);
  }, [preDispatchLiftFilter]);

  return (
    <Modal
      title="Decoded FrontEnd Messages"
      open={open}
      onCancel={onClose}
      width={1400}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: "80vh", overflow: "hidden" }}
      footer={[
        <Button
          key="download"
          onClick={onDownloadPreDispatchMessages}
          disabled={filteredPreDispatchMessages.length === 0}
        >
          Download JSON
        </Button>,
        <Button
          key="clear"
          onClick={() => {
            setPreDispatchMessages([]);
            setSelectedPreDispatchMessageIndex(null);
          }}
        >
          Clear
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginBottom: 12 }}>
        <Input
          placeholder="Filter by liftId"
          value={preDispatchLiftFilter}
          onChange={(e) => setPreDispatchLiftFilter(e.target.value)}
        />
      </div>
      <div
        style={{
          display: "grid",
          gap: 8,
        }}
      >
        <div>
          Messages received for this lift: <strong>{filteredPreDispatchMessages.length}</strong>
        </div>
        <div style={{ opacity: 0.75 }}>
          This debug view stores messages only while a liftId filter is set.
        </div>
      </div>

      <div
        style={{
          marginTop: 12,
          border: "1px solid #d9d9d9",
          borderRadius: 8,
          padding: 10,
          display: "grid",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>Service mode tag (debug)</div>
            {selectedServiceModeInfo ? (
              <Tooltip placement="top" title={selectedServiceModeInfo.displayName}>
                <Tag color={selectedServiceModeInfo.color || "default"}>
                  {selectedServiceModeInfo.abbreviation.substring(0, 4)}
                </Tag>
              </Tooltip>
            ) : (
              <Tag color="default">N/A</Tag>
            )}
            <Tooltip placement="topLeft" title={serviceModeInfoTooltip}>
              <InfoCircleOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} />
            </Tooltip>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>Traffic position (debug)</div>
            {selectedBitMaskTrafficPosition !== null ? (
              <Tag color="processing">{selectedBitMaskTrafficPosition}</Tag>
            ) : (
              <Tag color="default">N/A</Tag>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 600 }}>contentType filter</div>
            <Radio.Group
              value={contentTypeFilter}
              onChange={(e: RadioChangeEvent) =>
                setContentTypeFilter(e.target.value as ContentTypeFilter)
              }
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="all">All messages</Radio.Button>
              <Radio.Button value="OneHotMessage">OneHotMessage</Radio.Button>
              <Radio.Button value="BitMaskMessage">BitMaskMessage</Radio.Button>
            </Radio.Group>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "260px minmax(0, 1fr) minmax(0, 1fr)",
          gap: 12,
          marginTop: 12,
          alignItems: "start",
          maxHeight: "62vh",
        }}
      >
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            padding: 10,
            minHeight: 220,
            maxHeight: "56vh",
            overflowY: "auto",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Messages</div>
          <div style={{ display: "grid", gap: 6 }}>
            {filteredPreDispatchMessages.length === 0 ? (
              <div style={{ opacity: 0.75, fontSize: 12 }}>
                No messages for current liftId filter.
              </div>
            ) : (
              filteredPreDispatchMessages.map((entry, index) => {
                const isSelected = selectedPreDispatchMessageIndex === index;

                return (
                  <button
                    key={`${entry.timestamp}-${index}`}
                    type="button"
                    onClick={() => setSelectedPreDispatchMessageIndex(index)}
                    style={{
                      textAlign: "left",
                      border: isSelected ? "1px solid #1677ff" : "1px solid #d9d9d9",
                      borderRadius: 6,
                      background: isSelected ? "#e6f4ff" : "#fff",
                      padding: "8px 10px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>Message {index + 1}</div>
                    <div style={{ fontSize: 11, opacity: 0.75 }}>
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div
          style={{
            ...jsonBoxHighlightStyle,
            borderRadius: 8,
            padding: 10,
            minHeight: 220,
            maxHeight: "56vh",
            overflowY: "auto",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>schemaFrontEndMessage</div>
          <pre
            style={{
              margin: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: 12,
            }}
          >
            {latestSchemaFrontEndPrettyJson || "No messages yet for this liftId filter."}
          </pre>
        </div>

        <div
          style={{
            ...jsonBoxHighlightStyle,
            borderRadius: 8,
            padding: 10,
            minHeight: 220,
            maxHeight: "56vh",
            overflowY: "auto",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>schemaFrontEndMessageTranslated</div>
          <pre
            style={{
              margin: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: 12,
            }}
          >
            {latestSchemaFrontEndTranslatedPrettyJson || "No messages yet for this liftId filter."}
          </pre>
        </div>
      </div>
    </Modal>
  );
}

export default DecodedFrontEndMessagesModal;
